import { isTomorrow, differenceInDays } from 'date-fns';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Clock, AlertCircle, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatInTz, nowInTimezone, DEFAULT_TIMEZONE } from '@/utils/timezone';

interface FollowUpIndicatorProps {
  date: string | null | undefined;
  variant?: 'badge' | 'text' | 'compact';
  className?: string;
}

export type FollowUpStatus = 'overdue' | 'today' | 'tomorrow' | 'upcoming' | 'none';

export function getFollowUpStatus(date: string | null | undefined, timezone: string = DEFAULT_TIMEZONE): FollowUpStatus {
  if (!date) return 'none';

  const followUpDate = new Date(date);
  const now = nowInTimezone(timezone);

  // Set to start of day for comparison
  const followUpDay = new Date(followUpDate.getFullYear(), followUpDate.getMonth(), followUpDate.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (followUpDay < today) return 'overdue';
  if (followUpDay.getTime() === today.getTime()) return 'today';
  if (isTomorrow(followUpDate)) return 'tomorrow';
  return 'upcoming';
}

export function FollowUpIndicator({ date, variant = 'badge', className }: FollowUpIndicatorProps) {
  const status = getFollowUpStatus(date);

  if (status === 'none') {
    if (variant === 'text' || variant === 'compact') {
      return <span className="text-muted-foreground">-</span>;
    }
    return null;
  }

  const followUpDate = new Date(date!);
  const daysAgo = differenceInDays(nowInTimezone(DEFAULT_TIMEZONE), followUpDate);

  const statusConfig: Record<Exclude<FollowUpStatus, 'none'>, {
    label: string;
    badgeVariant: BadgeProps['variant'];
    badgeClassName?: string;
    icon: typeof Clock;
    textColor: string;
  }> = {
    overdue: {
      label: daysAgo === 1 ? 'Yesterday' : `${daysAgo}d overdue`,
      badgeVariant: 'destructive-soft',
      icon: AlertCircle,
      textColor: 'text-destructive',
    },
    today: {
      label: 'Today',
      badgeVariant: 'warning',
      icon: Clock,
      textColor: 'text-warning',
    },
    tomorrow: {
      label: 'Tomorrow',
      badgeVariant: 'info',
      icon: CalendarClock,
      textColor: 'text-info',
    },
    upcoming: {
      label: formatInTz(followUpDate, DEFAULT_TIMEZONE, 'MMM d'),
      badgeVariant: 'outline',
      badgeClassName: 'text-muted-foreground',
      icon: CalendarClock,
      textColor: 'text-muted-foreground',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  if (variant === 'compact') {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', config.textColor, className)}>
        <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-current" aria-hidden="true" />
        {config.label}
      </span>
    );
  }

  if (variant === 'text') {
    return (
      <span className={cn('flex items-center gap-1.5 text-sm', config.textColor, className)}>
        <Icon className="h-4 w-4" />
        {config.label}
      </span>
    );
  }

  return (
    <Badge variant={config.badgeVariant} className={cn('gap-1', config.badgeClassName, className)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}
